import React, { useState, useEffect } from "react";
import {
  TrashIcon,
  PlusIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import axiosInstance from "../../utils/axiosInstance";
import { Link } from "react-router-dom";
import Loader from "../../components/Loader";
import Notification from "../../components/Notification";
import { IoArrowBackSharp } from "react-icons/io5";

// Solution component
const Solution = () => {
  const [solutions, setSolutions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [solutionToDelete, setSolutionToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showNotification, setShowNotification] = useState(false);

  useEffect(() => {
    fetchSolutions();
  }, []);

  const fetchSolutions = async () => {
    try {
      const response = await axiosInstance.get("/solutions");
      setSolutions(response.data);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (solution) => {
    setSolutionToDelete(solution);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setButtonLoading(true);
      await axiosInstance.delete(`/solutions/${solutionToDelete.id}`);
      setSolutions(
        solutions.filter((solution) => solution.id !== solutionToDelete.id)
      );
      setShowDeleteModal(false);
      setButtonLoading(false);
      setSolutionToDelete(null);
      setShowNotification(true);
    } catch (err) {
      setError("Failed to delete solution. Please try again.");
    }
  };

  // Close the modal without deleting
  const handleCloseModal = () => {
    setShowDeleteModal(false);
    setSolutionToDelete(null);
  };

  // Filter solutions based on search term
  const filteredSolutions = solutions.filter(
    (solution) =>
      solution.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      solution.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      solution.type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="mx-auto max-w-[1500px] space-y-5 px-4 pb-4 sm:px-6 sm:pb-6">
        <Loader />
      </div>
    );
  }

  if (error) {
    return <p className="text-center text-sm font-medium text-rose-600 dark:text-rose-400">{error}</p>;
  }

  return (
    <div className="mx-auto max-w-[1500px] space-y-5 px-4 pb-4 sm:px-6 sm:pb-6">
      {/* Heading and Add Button */}

      <div className="flex justify-between items-center mb-4">
        <h3 className="mb-1 flex items-center gap-3 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
          {/* Go Back Button */}
          <button
            onClick={() => window.history.back()} // This will navigate back to the previous page
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-300 text-gray-500 transition hover:border-red-400/60 hover:text-red-500 dark:border-[#3a3a3d] dark:text-slate-400"
          >
            <IoArrowBackSharp size={24} />
          </button>
                <span className="h-6 w-1.5 shrink-0 rounded-full bg-gradient-to-b from-red-500 to-orange-400" />
          Solution List
        </h3>

        <div className="flex space-x-2">
          <Link
            to="/admin/add-solution"
            className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-red-600 to-orange-500 px-6 text-sm font-bold text-white shadow-lg shadow-red-500/25 transition hover:from-red-500 hover:to-orange-400 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add New Solution
          </Link>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-4">
        <input
          type="text"
          className="h-11 w-full rounded-[12px] border border-gray-300 bg-white px-4 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100 dark:placeholder:text-slate-500"
          placeholder="Search by name, code, or type..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <table className="min-w-full border-separate border-spacing-0 overflow-hidden rounded-[16px] border border-gray-200 bg-white text-sm dark:border-[#2c2c2e] dark:bg-[#1c1c1e]">
        <thead>
          <tr className="bg-gray-100 dark:bg-[#161617]">
            <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">Name</th>
            <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">Code</th>
            <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">Credits</th>
            <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">Type</th>
            <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">Order</th>
            <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredSolutions.map((solution) => (
            <tr key={solution.id} className="transition hover:bg-gray-50 dark:hover:bg-[#232325]">
              <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300">{solution.name}</td>
              <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300">{solution.code}</td>
              <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300">{solution.credits}</td>
              <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300">{solution.type}</td>
              <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300">{solution.order_no}</td>
              <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300 flex items-center gap-1">
                <button className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-red-500/10 hover:text-red-500 dark:hover:text-orange-400">
                  <Link to={`/admin/edit-solution/${solution.id}`}>
                    <PencilSquareIcon className="h-5 w-5" />
                  </Link>
                </button>
                <button
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-rose-500/10 hover:text-rose-500"
                  onClick={() => handleDeleteClick(solution)}
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md p-6 rounded-[22px] border border-gray-200 bg-white shadow-lg shadow-gray-900/5 dark:border-[#2c2c2e] dark:bg-[#1c1c1e] dark:shadow-black/40">
            <h2 className="mb-4 text-lg font-bold text-gray-900 dark:text-white">Are you sure?</h2>
            <p>
              Do you really want to delete the solution{" "}
              <strong>{solutionToDelete.name}</strong>? This action cannot be
              undone.
            </p>
            <div className="mt-6 flex justify-end space-x-4">
              <button
                className="inline-flex h-11 items-center justify-center gap-2 rounded-full border border-gray-300 bg-white px-5 text-sm font-semibold text-gray-700 transition hover:border-red-400/60 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-200"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
              <button
                className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-rose-500 px-6 text-sm font-bold text-white transition hover:bg-rose-400 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleDeleteConfirm}
                disabled={buttonLoading}
              >
                {buttonLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Popup */}
      {showNotification && (
        <Notification
          setShowNotification={setShowNotification}
          message={"Solution deleted successfully!"}
        />
      )}
    </div>
  );
};

export default Solution;
